import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  RadioGroup,
  FormControlLabel,
  Radio,
  Tooltip,
  alpha,
  Alert,
  Card,
  CardMedia,
  CardContent,
  Grid,
  useTheme
} from '@mui/material';
import {
  Checkroom as ClothesIcon,
  Visibility as VisibilityIcon,
  Style as StyleIcon,
  Category as CategoryIcon,
  ArrowForward as ArrowForwardIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import { useDesigner } from '../../contexts/DesignerContext';

// Icon types for garment transfer
const IconGeneric = () => <StyleIcon sx={{ fontSize: 30 }} />;
const IconUpper = () => <CategoryIcon sx={{ fontSize: 30 }} />;
const IconLower = () => <CategoryIcon sx={{ fontSize: 30 }} />;
const IconDress = () => <StyleIcon sx={{ fontSize: 30 }} />;
const IconTShirt = () => <StyleIcon sx={{ fontSize: 30 }} />;
const IconPants = () => <StyleIcon sx={{ fontSize: 30 }} />;
const IconJacket = () => <StyleIcon sx={{ fontSize: 30 }} />;
const IconBody = () => <VisibilityIcon sx={{ fontSize: 30 }} />;

// Types of garment transfer
const transferTypes = [
  { 
    value: 'generic', 
    label: 'Całe ubranie', 
    description: 'Rozpoznaj całe ubranie na zdjęciu', 
    maskPrompt: 'garment', 
    humanMaskPrompt: 't-shirt and trousers',
    IconComponent: IconGeneric,
    image: '/garment/body/full_body1.jpg'
  },
  { 
    value: 'upper', 
    label: 'Góra', 
    description: 'Przenieś górną część garderoby (T-shirt, bluza, koszula)', 
    maskPrompt: 't-shirt', 
    humanMaskPrompt: 't-shirt',
    IconComponent: IconUpper,
    image: '/garment/body/top.jpg'
  },
  { 
    value: 'lower', 
    label: 'Dół', 
    description: 'Przenieś dolną część garderoby (spodnie, spódnica)', 
    maskPrompt: 'trousers', 
    humanMaskPrompt: 'trousers',
    IconComponent: IconLower,
    image: '/garment/body/bottom.jpg'
  },
  { 
    value: 'dress', 
    label: 'Sukienka', 
    description: 'Przenieś sukienkę', 
    maskPrompt: 'dress', 
    humanMaskPrompt: 'dress',
    IconComponent: IconDress,
    image: '/garment/dress/dress1.jpg'
  },
  { 
    value: 't-shirt', 
    label: 'T-shirt', 
    description: 'Przenieś t-shirt', 
    maskPrompt: 't-shirt', 
    humanMaskPrompt: 't-shirt',
    IconComponent: IconTShirt,
    image: '/garment/t-shirt/t-shirt.jpg'
  },
  { 
    value: 'pants', 
    label: 'Spodnie', 
    description: 'Przenieś spodnie', 
    maskPrompt: 'pants', 
    humanMaskPrompt: 'pants',
    IconComponent: IconPants,
    image: '/garment/trousers/trousers.jpg'
  },
  { 
    value: 'jacket', 
    label: 'Kurtka/Płaszcz', 
    description: 'Przenieś kurtkę lub płaszcz', 
    maskPrompt: 'jacket', 
    humanMaskPrompt: 'jacket',
    IconComponent: IconJacket,
    image: '/garment/jacket/jacket.jpg'
  },
  { 
    value: 'shorts', 
    label: 'Szorty', 
    description: 'Przenieś szorty', 
    maskPrompt: 'shorts', 
    humanMaskPrompt: 'shorts',
    IconComponent: IconPants,
    image: '/garment/shorts/shorts1.jpg'
  },
  { 
    value: 'leggins', 
    label: 'Legginsy', 
    description: 'Przenieś legginsy', 
    maskPrompt: 'leggins', 
    humanMaskPrompt: 'leggins',
    IconComponent: IconPants,
    image: '/garment/leggins/leggins1.jpg'
  },
  { 
    value: 'top', 
    label: 'Top', 
    description: 'Przenieś top', 
    maskPrompt: 'top', 
    humanMaskPrompt: 'top',
    IconComponent: IconTShirt,
    image: '/garment/top/top.jpg'
  },
  { 
    value: 'suit', 
    label: 'Garnitur', 
    description: 'Przenieś garnitur', 
    maskPrompt: 'suit', 
    humanMaskPrompt: 'suit',
    IconComponent: IconJacket,
    image: '/garment/suite/suit.jpg'
  },
  { 
    value: 'body', 
    label: 'Transfer wyglądu', 
    description: 'Przeniosę odzież oraz stworzę osobę podobną do tej na zdjęciu.', 
    maskPrompt: 'body', 
    humanMaskPrompt: 'body',
    IconComponent: IconBody,
    image: '/garment/generated-model/real_generated_model2.jpg'
  }
];

interface GarmentTransferSelectionProps {
  onTransferTypeSelect?: (type: string, garmentMaskPrompt: string, humanMaskPrompt: string) => void;
  sourceImageUploaded?: boolean;
}

const GarmentTransferSelection: React.FC<GarmentTransferSelectionProps> = ({
  onTransferTypeSelect,
  sourceImageUploaded = false
}) => {
  const theme = useTheme();
  const { selections, setGarmentType, setParentCategory, setGarmentMaskPrompt, setHumanMaskPrompt, setTransferMode, setGarment } = useDesigner();
  const [selectedTransferType, setSelectedTransferType] = useState<string>(selections.garmentType || 'generic');
  
  // Track the main category separately for UI display purposes
  const [selectedMainCategory, setSelectedMainCategory] = useState<string>(() => {
    const currentType = selections.garmentType || 'generic';
    // Map specific garment types back to their main categories
    if (['t-shirt', 'jacket'].includes(currentType)) return 'upper';
    if (['pants', 'trousers'].includes(currentType)) return 'lower';
    if (['dress'].includes(currentType)) return 'dress';
    return currentType;
  });
  
  // Set initial transfer mode when component mounts
  React.useEffect(() => {
    // Default to 'garment' mode for most types
    const initialType = selections.garmentType || 'generic';
    if (initialType === 'body') {
      setTransferMode('body');
    } else {
      setTransferMode('garment');
    }
    
    // Set initial parentCategory for the current garmentType
    if (['t-shirt', 'jacket', 'top', 'suit'].includes(initialType)) {
      setParentCategory('upper');
    } else if (['pants', 'trousers', 'shorts', 'leggins'].includes(initialType)) {
      setParentCategory('lower');
    } else if (initialType === 'dress') {
      setParentCategory('generic');
    } else if (['generic', 'upper', 'lower', 'body'].includes(initialType)) {
      setParentCategory(initialType);
    } else {
      setParentCategory(null);
    }
    
    // Set initial prompt values
    const transferType = transferTypes.find(t => t.value === initialType);
    if (transferType) {
      setGarmentMaskPrompt(transferType.maskPrompt);
      setHumanMaskPrompt(transferType.humanMaskPrompt);
      
      // Notify parent
      if (onTransferTypeSelect) {
        onTransferTypeSelect(initialType, transferType.maskPrompt, transferType.humanMaskPrompt);
      }
    }
  }, []);

  const handleTransferTypeChange = (type: string) => {
    setSelectedTransferType(type);
    setGarmentType(type);
    
    // Update main category based on the selected type and set parentCategory for context
    let mainCategory = type;
    if (['t-shirt', 'jacket', 'top', 'suit'].includes(type)) {
      // Upper body garments
      mainCategory = 'upper';
      setSelectedMainCategory('upper');
      setParentCategory('upper'); // Set parent category for context tracking
    } else if (['pants', 'trousers', 'shorts', 'leggins'].includes(type)) {
      // Lower body garments
      mainCategory = 'lower';
      setSelectedMainCategory('lower');
      setParentCategory('lower'); // Set parent category for context tracking
    } else if (type === 'dress') {
      // Dress is its own category but should still use sourceImage for on-model transfers
      mainCategory = 'dress';
      setSelectedMainCategory('dress');
      setParentCategory('generic'); // Dress should use sourceImage like generic
    } else {
      setSelectedMainCategory(type);
      // For main categories (generic, upper, lower, body) set parentCategory to the same value
      if (['generic', 'upper', 'lower', 'body'].includes(type)) {
        setParentCategory(type);
      } else {
        setParentCategory(null); // For other specific types without main category
      }
    }
    
    // Set the appropriate mask prompts based on transfer type
    const transferType = transferTypes.find(t => t.value === type);
    if (transferType) {
      setGarmentMaskPrompt(transferType.maskPrompt);
      setHumanMaskPrompt(transferType.humanMaskPrompt);
      
      // Set appropriate transfer mode
      if (type === 'body') {
        setTransferMode('body');
      } else if (type === 'generic' || ['t-shirt', 'dress', 'pants', 'jacket', 'shorts', 'leggins', 'top', 'suit'].includes(type)) {
        setTransferMode('garment');
      } else {
        setTransferMode('specific');
      }
      
      // Automatically select default garment image only for specific garment types, not for main categories
      const defaultGarmentPaths: Record<string, string> = {
        't-shirt': '/garment/t-shirt/t-shirt.jpg',
        'jacket': '/garment/jacket/jacket.jpg',
        'pants': '/garment/trousers/trousers.jpg',
        'trousers': '/garment/trousers/trousers.jpg',
        'dress': '/garment/dress/dress1.jpg',
        'shorts': '/garment/shorts/shorts1.jpg',
        'leggins': '/garment/leggins/leggins1.jpg',
        'top': '/garment/top/top.jpg',
        'suit': '/garment/suite/suit.jpg'
        // Note: 'upper', 'lower', 'generic', 'body' should use sourceImage, not default paths
      };
      
      // Set default garment image only for specific garment types, not for main categories
      if (defaultGarmentPaths[type] && !['upper', 'lower', 'generic', 'body'].includes(type)) {
        setGarment(type, defaultGarmentPaths[type]);
      }
      
      // Debug logging
      console.log('=== GARMENT TRANSFER SELECTION ===');
      console.log('Selected type:', type);
      console.log('Main category:', mainCategory);
      
      let parentCategoryValue = null;
      if (['t-shirt', 'jacket', 'top', 'suit'].includes(type)) {
        parentCategoryValue = 'upper';
      } else if (['pants', 'trousers', 'shorts', 'leggins'].includes(type)) {
        parentCategoryValue = 'lower';
      } else if (type === 'dress') {
        parentCategoryValue = 'generic';
      } else if (['generic', 'upper', 'lower', 'body'].includes(type)) {
        parentCategoryValue = type;
      }
      
      console.log('Parent category set to:', parentCategoryValue);
      console.log('Will use sourceImage:', parentCategoryValue !== null);
      console.log('=== END GARMENT TRANSFER SELECTION ===');

      // If there's a callback for type selection, call it
      if (onTransferTypeSelect) {
        onTransferTypeSelect(type, transferType.maskPrompt, transferType.humanMaskPrompt);
      }
    }
  };

  // Group transfer types into categories for a cleaner UI
  const transferTypeGroups = [
    {
      name: '',
      types: [
        transferTypes.find(t => t.value === 'generic')!,
        transferTypes.find(t => t.value === 'upper')!,
        transferTypes.find(t => t.value === 'lower')!,
        transferTypes.find(t => t.value === 'body')!
      ]
    },
    {
      name: 'Konkretny element garderoby',
      types: [
        transferTypes.find(t => t.value === 't-shirt')!,
        transferTypes.find(t => t.value === 'top')!,
        transferTypes.find(t => t.value === 'dress')!,
        transferTypes.find(t => t.value === 'jacket')!,
        transferTypes.find(t => t.value === 'suit')!,
        transferTypes.find(t => t.value === 'pants')!,
        transferTypes.find(t => t.value === 'shorts')!,
        transferTypes.find(t => t.value === 'leggins')!
      ]
    }
  ];

  return (
    <Paper elevation={2} sx={{ p: 0, mb: 4, borderRadius: 3, overflow: 'hidden', boxShadow: '0 6px 18px rgba(0, 0, 0, 0.08)' }}>
      {/* Header Section */}
      <Box sx={{ 
        p: 3, 
        borderBottom: '1px solid rgba(0, 0, 0, 0.1)', 
        backgroundColor: 'rgba(25, 118, 210, 0.03)', 
        display: 'flex', 
        alignItems: 'center' 
      }}>
        <ClothesIcon sx={{ mr: 1.5, color: 'primary.main', fontSize: 28 }} />
        <Typography variant="h5" fontWeight={600} sx={{ color: 'primary.main', letterSpacing: '-0.01em' }}>
          Wybierz rodzaj przeniesienia odzieży:
        </Typography>
      </Box>
      
      <Box sx={{ p: 3 }}>
        {!sourceImageUploaded && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Najpierw załaduj zdjęcie produktu, aby móc wybrać rodzaj przeniesienia.
          </Alert>
        )}
        
        {sourceImageUploaded && (
          <>
            
            {/* Card Grid for Transfer Type Selection */}
            {transferTypeGroups.map((group, groupIndex) => {
              // For 'body' transfer, disallow choosing specific garment elements
              if (group.name === 'Konkretny element garderoby' && selectedMainCategory === 'body') {
                return null;
              }
              return (
                <Box key={`group-${groupIndex}`} sx={{ mb: 4 }}>
                <Typography 
                  variant="subtitle2" 
                  sx={{ 
                    mb: 2, 
                    color: 'text.secondary',
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                    pb: 0.5
                  }}
                >
                  {group.name}
                </Typography>
                
                <Grid container spacing={2}>
                  {group.types.map((type) => (
                    <Grid item xs={12} sm={['upper', 'lower', 'generic'].includes(type.value) ? 12 : 6} md={['upper', 'lower', 'generic'].includes(type.value) ? 4 : 3} key={type.value}>
                      <Card 
                        sx={{ 
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          position: 'relative',
                          transform: (selectedTransferType === type.value || selectedMainCategory === type.value) ? 'scale(1.02)' : 'scale(1)',
                          border: (selectedTransferType === type.value || selectedMainCategory === type.value)
                            ? `2px solid ${theme.palette.primary.main}` 
                            : '1px solid rgba(0,0,0,0.12)',
                          boxShadow: (selectedTransferType === type.value || selectedMainCategory === type.value)
                            ? `0 4px 20px ${alpha(theme.palette.primary.main, 0.25)}` 
                            : '0 1px 4px rgba(0,0,0,0.08)',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: '0 6px 20px rgba(0,0,0,0.15)'
                          }
                        }}
                        onClick={() => handleTransferTypeChange(type.value)}
                      >
                        {/* Selection indicator */}
                        {(selectedTransferType === type.value || selectedMainCategory === type.value) && (
                          <Box sx={{
                            position: 'absolute',
                            top: 10,
                            right: 10,
                            backgroundColor: theme.palette.primary.main,
                            borderRadius: '50%',
                            width: 26,
                            height: 26,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.2)'
                          }}>
                            <CheckIcon sx={{ color: 'white', fontSize: 18 }} />
                          </Box>
                        )}
                        
                        <Box sx={{ 
                          p: type.value === 'generic' ? '1.5rem 2rem' : type.value === 'upper' ? '1.5rem 2rem' : type.value === 'lower' ? '1.5rem 1rem' : 3, 
                          display: 'flex', 
                          flexDirection: 'column', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          backgroundColor: (selectedTransferType === type.value || selectedMainCategory === type.value)
                            ? alpha(theme.palette.primary.main, 0.1) 
                            : 'transparent'
                        }}>
                          {type.image ? (
                            // Image thumbnail if available
                            <Box sx={{
                              width: type.value === 'generic' ? 180 : type.value === 'upper' ? 200 : type.value === 'lower' ? 220 : 80,
                              height: type.value === 'generic' ? 240 : type.value === 'upper' ? 100 : type.value === 'lower' ? 110 : 80,
                              mb: ['upper', 'lower', 'generic'].includes(type.value) ? 3 : 2,
                              position: 'relative',
                              overflow: 'hidden',
                              borderRadius: 2,
                              border: (selectedTransferType === type.value || selectedMainCategory === type.value)
                                ? `2px solid ${theme.palette.primary.main}` 
                                : `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                            }}>
                              <img 
                                src={type.image} 
                                alt={type.label}
                                style={{ 
                                  width: '100%', 
                                  height: '100%',
                                  objectFit: 'cover',
                                  display: 'block'
                                }}
                              />
                            </Box>
                          ) : (
                            // Fallback to icon if no image
                            <Box sx={{ 
                              backgroundColor: (selectedTransferType === type.value || selectedMainCategory === type.value)
                                ? theme.palette.primary.main 
                                : alpha(theme.palette.primary.main, 0.1),
                              width: 60,
                              height: 60,
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mb: 2
                            }}>
                              {type.IconComponent && <type.IconComponent />}
                            </Box>
                          )}
                          
                          <Typography 
                            variant="body1" 
                            fontWeight={(selectedTransferType === type.value || selectedMainCategory === type.value) ? 600 : 500}
                            align="center"
                            sx={{ 
                              color: (selectedTransferType === type.value || selectedMainCategory === type.value)
                                ? theme.palette.primary.main 
                                : 'text.primary'
                            }}
                          >
                            {type.label}
                          </Typography>
                        </Box>
                        
                        <CardContent sx={{ 
                          pt: 1, 
                          pb: '8px !important',
                          borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                        }}>
                          <Typography 
                            variant="caption" 
                            color="text.secondary" 
                            component="div"
                            align="center"
                          >
                            {type.description}
                          </Typography>
                          
                          {type.value === 'body' && (
                            <Typography 
                              variant="caption" 
                              component="div" 
                              sx={{ 
                                display: 'block', 
                                color: 'warning.main', 
                                mt: 0.5,
                                textAlign: 'center',
                                fontWeight: 500
                              }}
                            >
                              Przeniosę odzież oraz stworzę osobę podobną do tej na zdjęciu.
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
              );
            })}
            
            {/* Visual example of selection */}
            <Box sx={{ mt: 4, pt: 3, borderTop: `1px dashed ${alpha(theme.palette.primary.main, 0.3)}` }}>
              <Typography variant="subtitle1" gutterBottom>
                Twój wybór: <strong>{transferTypes.find(t => t.value === selectedTransferType)?.label || 'Brak'}</strong>
              </Typography>
              
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Po wybraniu rodzaju przeniesienia, przejdź do następnego kroku, aby wybrać docelowego modela.
              </Typography>
              
              <Box sx={{ 
                mt: 2, 
                display: 'flex', 
                alignItems: 'center',
                color: theme.palette.primary.main,
                fontWeight: 600
              }}>
                <ArrowForwardIcon sx={{ ml: 1, fontSize: 20 }} />
              </Box>
            </Box>
          </>
        )}
      </Box>
    </Paper>
  );
};

export default GarmentTransferSelection;
